import React, { useState } from 'react';
import { UserRole } from '../types/user';

interface Permission {
  id: string;
  name: string;
  description: string;
  category: string;
}

interface RolePermissions {
  role: UserRole;
  label: string;
  description: string;
  permissions: string[];
  color: string;
}

const Admin_RoleManagement: React.FC = () => {
  const allPermissions: Permission[] = [
    // Eventos
    { id: 'events.view', name: 'Ver eventos', description: 'Puede ver la lista de eventos', category: 'Eventos' },
    { id: 'events.create', name: 'Crear eventos', description: 'Puede crear nuevos eventos', category: 'Eventos' },
    { id: 'events.edit', name: 'Editar eventos', description: 'Puede modificar eventos existentes', category: 'Eventos' },
    { id: 'events.delete', name: 'Eliminar eventos', description: 'Puede eliminar eventos', category: 'Eventos' },
    { id: 'events.publish', name: 'Publicar eventos', description: 'Puede publicar/despublicar eventos', category: 'Eventos' },
    
    // Stands
    { id: 'stands.view', name: 'Ver stands', description: 'Puede ver información de stands', category: 'Stands' },
    { id: 'stands.reserve', name: 'Reservar stands', description: 'Puede reservar stands en eventos', category: 'Stands' },
    { id: 'stands.manage', name: 'Gestionar stands', description: 'Puede crear y modificar stands', category: 'Stands' },
    { id: 'stands.approve', name: 'Aprobar reservas', description: 'Puede aprobar/rechazar reservas de stands', category: 'Stands' },
    
    // Usuarios
    { id: 'users.view', name: 'Ver usuarios', description: 'Puede ver la lista de usuarios', category: 'Usuarios' },
    { id: 'users.create', name: 'Crear usuarios', description: 'Puede crear nuevos usuarios', category: 'Usuarios' },
    { id: 'users.edit', name: 'Editar usuarios', description: 'Puede modificar información de usuarios', category: 'Usuarios' },
    { id: 'users.delete', name: 'Eliminar usuarios', description: 'Puede eliminar usuarios', category: 'Usuarios' },
    { id: 'users.roles', name: 'Gestionar roles', description: 'Puede asignar y modificar roles', category: 'Usuarios' },
    
    // Reportes
    { id: 'reports.view', name: 'Ver reportes', description: 'Puede ver reportes y estadísticas', category: 'Reportes' },
    { id: 'reports.export', name: 'Exportar reportes', description: 'Puede exportar reportes', category: 'Reportes' },
    { id: 'reports.financial', name: 'Reportes financieros', description: 'Puede ver reportes financieros', category: 'Reportes' },
    
    // Sistema
    { id: 'system.settings', name: 'Configuración', description: 'Puede modificar configuración del sistema', category: 'Sistema' },
    { id: 'system.logs', name: 'Ver logs', description: 'Puede ver logs del sistema', category: 'Sistema' },
  ];

  const [roles, setRoles] = useState<RolePermissions[]>([
    {
      role: 'admin',
      label: 'Administrador',
      description: 'Control total del sistema',
      color: 'purple',
      permissions: allPermissions.map(p => p.id), // Todos los permisos
    },
    {
      role: 'organizador',
      label: 'Organizador',
      description: 'Gestiona eventos y stands',
      color: 'blue',
      permissions: [
        'events.view', 'events.create', 'events.edit', 'events.publish',
        'stands.view', 'stands.manage', 'stands.approve',
        'users.view',
        'reports.view', 'reports.export',
      ],
    },
    {
      role: 'emprendedor',
      label: 'Emprendedor',
      description: 'Participa en eventos',
      color: 'green',
      permissions: [
        'events.view',
        'stands.view', 'stands.reserve',
        'reports.view',
      ],
    },
  ]);

  const [selectedRole, setSelectedRole] = useState<UserRole>('admin');

  const selectedRoleData = roles.find(r => r.role === selectedRole);

  const togglePermission = (permissionId: string) => {
    setRoles(roles.map(role => {
      if (role.role === selectedRole) {
        const hasPermission = role.permissions.includes(permissionId);
        return {
          ...role,
          permissions: hasPermission
            ? role.permissions.filter(p => p !== permissionId)
            : [...role.permissions, permissionId]
        };
      }
      return role;
    }));
  };

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; text: string; border: string }> = {
      purple: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
      blue: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
      green: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
    };
    return colors[color] || colors.purple;
  };

  const groupedPermissions = allPermissions.reduce((acc, permission) => {
    if (!acc[permission.category]) {
      acc[permission.category] = [];
    }
    acc[permission.category].push(permission);
    return acc;
  }, {} as Record<string, Permission[]>);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Gestión de Roles y Permisos</h1>
        <p className="text-gray-600">Configura los permisos para cada rol del sistema</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Roles List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Roles del Sistema</h2>
            </div>
            <div className="p-4 space-y-3">
              {roles.map((role) => {
                const colors = getColorClasses(role.color);
                const isSelected = selectedRole === role.role;
                return (
                  <button
                    key={role.role}
                    onClick={() => setSelectedRole(role.role)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      isSelected
                        ? `${colors.bg} ${colors.border} shadow-md`
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className={`font-semibold ${isSelected ? colors.text : 'text-gray-900'}`}>
                        {role.label}
                      </h3>
                      <span className="text-sm text-gray-500">
                        {role.permissions.length} permisos
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{role.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Stats Card */}
          <div className="bg-white rounded-lg shadow p-4 mt-6">
            <h3 className="font-semibold text-gray-900 mb-3">Estadísticas</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Total de permisos:</span>
                <span className="font-semibold">{allPermissions.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Categorías:</span>
                <span className="font-semibold">{Object.keys(groupedPermissions).length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Roles configurados:</span>
                <span className="font-semibold">{roles.length}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Permissions Panel */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Permisos de {selectedRoleData?.label}
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedRoleData?.description}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900">
                    {selectedRoleData?.permissions.length}
                  </div>
                  <div className="text-xs text-gray-500">permisos activos</div>
                </div>
              </div>
            </div>

            <div className="p-4">
              {Object.entries(groupedPermissions).map(([category, permissions]) => (
                <div key={category} className="mb-6 last:mb-0">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3 flex items-center">
                    <span className="bg-gray-200 rounded px-2 py-1">{category}</span>
                    <span className="ml-2 text-xs text-gray-500">
                      ({permissions.filter(p => selectedRoleData?.permissions.includes(p.id)).length}/{permissions.length})
                    </span>
                  </h3>
                  <div className="space-y-2">
                    {permissions.map((permission) => {
                      const hasPermission = selectedRoleData?.permissions.includes(permission.id);
                      return (
                        <label
                          key={permission.id}
                          className={`flex items-start p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            hasPermission
                              ? 'bg-blue-50 border-blue-200'
                              : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={hasPermission}
                            onChange={() => togglePermission(permission.id)}
                            className="mt-1 h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <div className="ml-3 flex-1">
                            <div className="font-medium text-gray-900">{permission.name}</div>
                            <div className="text-sm text-gray-600">{permission.description}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  Los cambios se guardan automáticamente
                </div>
                <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium">
                  Guardar Cambios
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin_RoleManagement;
